'use client';

import CustomButton from '@/components/custom-button';
import CustomLoading from '@/components/custom-loading';
import TableAG, { ColDef, TableAGRef } from '@/components/custom-table-ag';
import CustomToast from '@/components/custom-toast';
import { template_error } from '@/components/custom-toast/sonner-with-template';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { dynamicSort } from '@/lib/random';
import { scrollElementIntoView } from '@/lib/scroll';
import {
    cn,
    convertMinutesTo,
    formatDateTime,
    getDossierStatusResourceCode,
} from '@/lib/utils';
import { cust_FindDossierByReceiptNumber } from '@/server/api/v1/dossier/dossier.action';
import { DossierDetailCustResponse } from '@/server/api/v1/dossier/type';
import {
    ImplementationStep,
    ProcedureResponse,
} from '@/server/api/v1/procedure/type';
import { api } from '@/trpc/client';
import { DossierDetailProcessingStatus, DossierStatus } from '@/types/enum';
import { Component, Search } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';

type DataListDossier = {
    label: string;
    component?: React.ReactElement;
};

const { sonner } = CustomToast;

/**
 * Số lần tối đa có thể gọi API tìm kiếm trong 1 phút
 *
 */
const MaxCallAPIIn1Min = 5;

const DossierLookUpOfCust = () => {
    const transDef = useTranslations('System');
    const transPage = useTranslations('LookUpDossier');
    const transDossier = useTranslations('DossierManagement');

    const _totalCallIn1Min = useRef<number>(0);
    const _timeoutToResetCall = useRef<NodeJS.Timeout>();

    const apiUtils = api.useUtils();

    const _scrollDivRef = useRef<HTMLDivElement>(null);
    const _inputRef = useRef<HTMLInputElement>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [dossierResult, setDossierResult] =
        useState<DossierDetailCustResponse>();
    const [procedureInfo, setProcedureInfo] = useState<ProcedureResponse>();
    const [isDossierPause, setIsDossierPause] = useState(false);
    const [currentStepID, setCurrentStepID] = useState<string>();

    const onFindDossierByReceipt = async () => {
        let dossierCode: string = '';
        if (_inputRef.current) {
            dossierCode = _inputRef.current.value?.trim();
        }

        if (!dossierCode) {
            CustomToast.sonner.warning(
                transPage('LookUpDossier_PlsEnterReceiveNumber')
            );
            return;
        }
        if (dossierCode.length < 12) {
            CustomToast.sonner.warning(
                transPage('LookUpDossier_InvalidReceiveNumber')
            );
            return;
        }

        setDossierResult(undefined);
        setProcedureInfo(undefined);

        setIsLoading(true);

        const result = await cust_FindDossierByReceiptNumber(dossierCode);

        setIsLoading(false);

        if (result?.data?.isSuccessStatus) {
            if (result.data.data?.Result) {
                const dossier = {
                    ...result.data.data.Result,
                } as DossierDetailCustResponse;

                setIsDossierPause(
                    !!(
                        dossier.DossierSummaryProcess &&
                        dossier.DossierSummaryProcess.Pause_IsActive
                    )
                );
                setProcedureInfo(dossier.Procedure ?? undefined);
                setDossierResult(dossier);

                let latestStepID: string | undefined = undefined;

                const arrStep = dossier.Procedure.ImplementationProcesses?.find(
                    (x) => x.IsProcedureDef
                )?.ImplementationSteps;

                if (dossier.Status === DossierStatus.WaitingForReturn) {
                    latestStepID = arrStep?.[arrStep?.length - 1]?.ID;
                } else if (dossier.DossierDetailProcesses?.length) {
                    const latestDetailProcess =
                        dossier.DossierDetailProcesses.findLast((x) => {
                            return (
                                x?.ProcessingStatus ===
                                DossierDetailProcessingStatus.Pending
                            );
                        });

                    if (latestDetailProcess && arrStep?.length) {
                        if (latestDetailProcess.IsInitial) {
                            latestStepID = arrStep[0]?.ID;
                        } else {
                            const currentStep = arrStep.find(
                                (x) =>
                                    x.Code ===
                                    latestDetailProcess.Step_CurrentCode
                            );

                            latestStepID = currentStep?.ID;
                        }
                    }
                }

                setCurrentStepID(latestStepID);

                // Cuộn tới khu vực kết quả tra cứu sau khi load xong;
                // chỉ cuộn vùng nội dung chứ không kéo cả trang lên.
                setTimeout(() => {
                    scrollElementIntoView(_scrollDivRef.current, {
                        block: 'start',
                    });
                }, 200);
            } else {
                sonner.info(transPage('LookUpDossier_DossierNotFound'));
            }
        } else {
            console.error(`Failed when looking up dossier: `, result?.data);
            sonner.error(
                template_error({
                    title: transDef('System_AnErrorHasOccurred'),
                    desc: result?.data?.errors?.join(', ') ?? '',
                })
            );
        }
    };

    const dataListRender: DataListDossier[] = useMemo(() => {
        if (!dossierResult || !procedureInfo) {
            return [];
        }
        return [
            {
                label: transPage('LookUpDossier_ProcedureName'),
                component: <p>{procedureInfo?.Name}</p>,
            },
            {
                label: transPage('LookUpDossier_Summary'),
                component: <p>{dossierResult.Summary}</p>,
            },
            {
                label: transPage('LookUpDossier_ReceiveNumber'),
                component: <p>{dossierResult.Receiving_Number}</p>,
            },
            // {
            //     label: 'Người nộp hồ sơ',
            //     component: <p></p>,
            // },
            {
                label: transPage('LookUpDossier_ReceiveDate'),
                component: (
                    <p>{formatDateTime(dossierResult.Receiving_Date)}</p>
                ),
            },
            {
                label: transPage('LookUpDossier_DueDate'),
                component: <p>{formatDateTime(dossierResult.Deadline_Date)}</p>,
            },
            {
                label: transPage('LookUpDossier_Status'),
                component: (
                    <Badge variant={isDossierPause ? 'destructive' : 'default'}>
                        {transDossier(
                            isDossierPause
                                ? 'DossierStatus_StopCounting'
                                : getDossierStatusResourceCode(
                                      dossierResult.Status
                                  )
                        )}
                    </Badge>
                ),
            },
        ];
    }, [dossierResult, isDossierPause, procedureInfo, transDossier, transPage]);

    return (
        <div className='min-h-60rounded-sm mx-auto bg-primary-foreground p-5 shadow-md'>
            <h2 className='mb-10 mt-4 text-center text-2xl font-semibold uppercase text-primary'>
                {transPage('LookUpDossier_LookUpDossierInfo')}
            </h2>

            <div className='mt-4 flex justify-center gap-10 max-lg:block'>
                <div className='flex items-center gap-2 max-lg:mb-4'>
                    <div className='flex h-10 min-h-10 w-10 min-w-10 items-center justify-center rounded-full bg-primary/90 text-white'>
                        1
                    </div>
                    <div>
                        {transPage(
                            'LookUpDossier_EnterTheCorrectProfileCodeToSearchFor'
                        )}
                    </div>
                </div>
                <div className='flex items-center gap-2'>
                    <div className='flex h-10 min-h-10 w-10 min-w-10 items-center justify-center rounded-full bg-primary/90 text-white'>
                        2
                    </div>
                    <div>
                        {transPage(
                            'LookUpDossier_ClickProfileSearchToPerformAProfileSearch'
                        )}
                    </div>
                </div>
            </div>
            <div className='mt-10 flex select-none justify-center gap-4 max-md:flex-col'>
                <Input
                    className='no-ring max-w-3xl border-2 font-medium'
                    placeholder={
                        transPage('LookUpDossier_EnterTheReceiveNumber') + '...'
                    }
                    readOnly={isLoading}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            onFindDossierByReceipt();
                        }
                    }}
                    ref={_inputRef}
                />
                <CustomButton.Root
                    onClick={onFindDossierByReceipt}
                    icon={<Search size={16} />}
                    isLoading={isLoading}
                >
                    {transPage('LookUpDossier_LookUpDossier')}
                </CustomButton.Root>
            </div>

            <CustomLoading.Wrapper
                className={cn(
                    'mt-5 min-h-40 border-t p-5 text-base max-lg:px-0',
                    {
                        'border-transparent': !dossierResult,
                    }
                )}
                loading={isLoading}
                description={
                    <CustomLoading.Dot>
                        {transPage('LookUpDossier_IsLookupDossier')}
                    </CustomLoading.Dot>
                }
            >
                <div ref={_scrollDivRef}>
                    {dossierResult && (
                        <>
                            <div>
                                <div className='mb-2 flex items-center gap-2 text-lg font-semibold text-primary'>
                                    <Component
                                        size={16}
                                        className='text-primary'
                                    />
                                    {transPage('LookUpDossier_DossierInfo')}
                                </div>
                                <div className='pl-2 lg:pl-5'>
                                    {dataListRender.map((x, key) => {
                                        return (
                                            <div
                                                className='mb-2 grid max-w-full grid-cols-12 items-center gap-x-4 gap-y-1'
                                                key={key}
                                            >
                                                <p className='col-span-full font-medium lg:col-span-3 lg:text-right'>
                                                    {x.label}:
                                                </p>
                                                <div className='col-span-full lg:col-span-9'>
                                                    {x.component}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <div className='mt-4 flex items-center gap-2 text-lg font-semibold text-primary'>
                                    <Component
                                        size={16}
                                        className='text-primary'
                                    />
                                    {transPage('LookUpDossier_ImplementStep')}
                                </div>
                                {procedureInfo && (
                                    <RenderStepOfProcedure
                                        procedureInfo={procedureInfo}
                                        currentImplementStepId={currentStepID}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </CustomLoading.Wrapper>
        </div>
    );
};

const RenderStepOfProcedure = ({
    procedureInfo,
    currentImplementStepId,
}: {
    procedureInfo: ProcedureResponse;
    currentImplementStepId?: string;
}) => {
    const transPage = useTranslations('LookUpDossier');
    const transDef = useTranslations('System');
    const _tableRef = useRef<TableAGRef>(null);

    const [steps, setSteps] = useState<ImplementationStep[]>([]);

    useEffect(() => {
        if (procedureInfo.ImplementationProcesses?.length) {
            const implementProcessDef =
                procedureInfo.ImplementationProcesses.find(
                    (x) => x.IsProcedureDef
                );
            if (
                implementProcessDef &&
                implementProcessDef.ImplementationSteps
            ) {
                setSteps(
                    implementProcessDef.ImplementationSteps.sort(
                        dynamicSort('Ordinal')
                    )
                );
            } else {
                setSteps([]);
            }
        }
    }, [procedureInfo.ImplementationProcesses]);

    const [columnDefinations, setColumnDefinations] = useState<
        ColDef<ImplementationStep>[]
    >([
        {
            headerName: transDef('System_Ordinal'),
            minWidth: 100,
            maxWidth: 100,
            headerClass: 'font-medium py-2 text-base',
            cellClass: '[&_*]:h-full',
            suppressMovable: true,
            cellRenderer: (params) => {
                return (
                    <div className='flex h-full items-center justify-center text-wrap text-base'>
                        {(params.node?.rowIndex ?? 0) + 1}
                    </div>
                );
            },
            wrapText: true,
            sortable: false,
        },
        {
            headerName: transPage('LookUpDossier_StepName'),
            minWidth: 400,
            suppressMovable: true,
            headerClass: 'font-medium py-2 text-base',
            cellClass: '[&_*]:h-full',
            cellRenderer: (params) => {
                return (
                    <div className='flex h-full items-center text-wrap text-base'>
                        {params.data?.Name}
                    </div>
                );
            },
        },
        {
            headerName: transPage('LookUpDossier_Responsibility'),
            minWidth: 230,
            maxWidth: 230,
            suppressMovable: true,
            headerClass: 'font-medium py-2 text-base',
            cellClass: '[&_*]:h-full',
            cellRenderer: (params) => {
                return (
                    <div className='flex h-full items-center text-wrap text-base'>
                        {params.data?.Description}
                    </div>
                );
            },
        },
        {
            headerName: transPage('LookUpDossier_TimeToComplete'),
            minWidth: 230,
            maxWidth: 230,
            suppressMovable: true,
            headerClass: 'font-medium py-2 text-base',
            cellClass: '[&_*]:h-full',
            cellRenderer: (params) => {
                const { days } = convertMinutesTo(params.data?.TotalMinutes);
                return (
                    <div className='flex h-full items-center text-wrap text-base'>
                        {days
                            ? `${days} ${transPage('LookUpDossier_Days')}`
                            : ''}
                    </div>
                );
            },
        },
    ]);

    return (
        <TableAG.Root
            ref={_tableRef}
            hidePagination
            showCheckbox={false}
            rowData={steps}
            columnDefs={columnDefinations}
            //TODO: bỏ cái này là lỗi - hàm để xét lại danh sách checkbox và dùng để lấy lại cột khi kéo ra ngoài
            onColumnVisibilityChange={(arrCol) => {
                if (arrCol) {
                    setColumnDefinations(arrCol);
                }
            }}
            suppressCellFocus
            suppressMoveWhenRowDragging
            suppressRowClickSelection
            headerHeight={50}
            rowHeight={55}
            getRowStyle={({ data }) => {
                if (data?.ID === currentImplementStepId) {
                    return {
                        color: 'red',
                        fontWeight: '600',
                    };
                }
            }}
        />
    );
};
export default DossierLookUpOfCust;
